import { useUserEventLogger } from '@repo/tracking-service';
import cn from 'clsx';
import { kebabCase } from 'es-toolkit';
import { useRouter } from 'next/router';
import { useEffect, useState } from 'react';
import { PreloadedQuery, usePreloadedQuery } from 'react-relay';

import { businessSignboardQuery, NewSignboardType } from '@/__generated__/businessSignboardQuery.graphql';
import { SurveyTopNavigation } from '@/components/survey/common/TopNavigation';
import { useSurveyUserEventAttributes } from '@/components/survey/common/hooks/use-survey-user-event-attributes';
import { AnswerList, CTA, ProgressBar } from '@/components/survey/common/ui';
import common from '@/components/survey/common/ui/styles/common.module.scss';
import { upsertNewSignboard } from '@/graphql/mutation/survey/business';
import { signboardQuery } from '@/graphql/query/survey/business';
import { SURVEY_PAGES } from '@/lib/constants/paths';
import { useUpsertBusinessAnswer } from '@/lib/hooks/api/mutation/survey/use-upsert-business-answer';

const answers: { value: NewSignboardType; label: string }[] = [
  { value: 'newSignboard', label: '간판을 새로 만들었어요' },
  { value: 'takingOver', label: '기존에 있던 간판을 이용했어요' },
  { value: 'noHave', label: '창업할 때 간판이 없었어요' }
];

export const SignboardContainer = ({ queryRef }: { queryRef: PreloadedQuery<businessSignboardQuery> }) => {
  const router = useRouter();
  const { sendClickEvent } = useUserEventLogger();
  const eventAttributes = useSurveyUserEventAttributes();

  const { getSurveyBusinessAnswer, getSurveyBusinessInfo } = usePreloadedQuery(signboardQuery, queryRef);
  const business = getSurveyBusinessAnswer.businessAnswer?.[0];

  const [upsertBusinessAnswer, isLoading] = useUpsertBusinessAnswer(upsertNewSignboard);

  const [selectedAnswer, setSelectedAnswer] = useState<NewSignboardType | undefined>(undefined);

  useEffect(() => {
    if (getSurveyBusinessAnswer.result && business) {
      setSelectedAnswer(business?.isNewSignboard || undefined);
    }
  }, [business, getSurveyBusinessAnswer.result]);

  return (
    <>
      <SurveyTopNavigation leftClickEvent={'survey_signboard_back'} />
      <main className={cn(common.main_layout, common.main_cta_space)}>
        <ProgressBar step={1} />
        <form
          onSubmit={(e) => {
            e.preventDefault();
            sendClickEvent('survey_signboard_confirm', eventAttributes);

            upsertBusinessAnswer(
              { isNewSignboard: selectedAnswer },
              {
                onCompleted: () => {
                  router.push(SURVEY_PAGES.공동사업자);
                }
              }
            );
          }}
        >
          <article>
            <section>
              <h1 className={common.content_title}>
                {getSurveyBusinessInfo?.business?.[0]?.ofbDt?.slice(2, 4)}년도에 창업하실 때<br />
                사업장 간판을 새로 만드셨나요?
              </h1>

              <AnswerList
                answers={answers}
                checkedValue={selectedAnswer}
                onChange={(value) => {
                  sendClickEvent(`survey_signboard_${kebabCase(value)}`, eventAttributes);

                  setSelectedAnswer(value);
                }}
              />
            </section>
          </article>

          {selectedAnswer && (
            <CTA type={'submit'} disabled={isLoading}>
              다음
            </CTA>
          )}
        </form>
      </main>
    </>
  );
};
